(ns minorodata.dependency-graph.timeline.events
  "Execution-timeline state for the dependency graph (plain re-frame db).

  Stored under `[:dependency-graph-timeline <master-id>]`. The currently-open pane
  (which transform's timeline is shown) is tracked separately in
  `minorodata.dependency-graph.events` under `[:dependency-graph :timeline]`."
  (:require
   ["date-fns" :refer [addHours addSeconds differenceInHours differenceInMinutes
                       differenceInSeconds formatISO parseISO subHours]]
   [re-frame.core :as re-frame]))


(defn window-minutes
  "Whole minutes spanned by a window."
  [{:keys [started-at ended-at]}]
  (js/Math.round (differenceInMinutes ended-at started-at)))

(defn window-hours
  "Whole hours spanned by a window."
  [window]
  (js/Math.round (/ (window-minutes window) 60)))


(def min-window-hours 1)

;; kioku's own ceiling (`range-error`). Past it every fetch fails, and the grid
;; would be drawing thousands of gridlines to show it.
(def max-window-hours (* 366 24))

(defn at-min-zoom?
  "Whether zooming in further is a no-op, so the control can disable itself."
  [{:keys [started-at ended-at]}]
  (<= (differenceInHours (addSeconds ended-at 1) started-at) min-window-hours))

(defn at-max-zoom?
  "Whether zooming out further is a no-op."
  [{:keys [started-at ended-at]}]
  (>= (differenceInHours ended-at started-at) max-window-hours))


(defn zoom
  "Shrink (`:in`) or grow (`:out`) a window by ~20%, in whole hours. Zooming in
   bottoms out at one hour — hence the 0 and the asymmetric two-hour step."
  [direction {:keys [started-at ended-at]}]
  (case direction
    :in  (let [diff         (differenceInHours (addSeconds ended-at 1) started-at)
               change       (if (<= diff 1) 0 (max 1 (js/Math.round (/ (* diff 0.2) 2))))
               start-change (if (= diff 2) 0 change)
               end-change   (if (= diff 2) 1 change)]
           {:started-at (addHours started-at start-change)
            :ended-at   (subHours ended-at end-change)})
    ;; Clamped: each click compounds ~1.2x now that they accumulate instead of
    ;; being collapsed by a debounce, so twenty of them would ask for a year.
    :out (let [diff   (differenceInHours ended-at started-at)
               room   (js/Math.floor (/ (max 0 (- max-window-hours diff)) 2))
               change (min (max 1 (js/Math.round (/ (* diff 0.2) 2))) room)]
           (if (pos? change)
             {:started-at (subHours started-at change)
              :ended-at   (addHours ended-at change)}
             {:started-at started-at :ended-at ended-at}))))


(defn pan
  "Shift a window half a span backwards (`:back`) or forwards (`:forward`). Half,
   not a full span, so something the reader was looking at stays on screen. In
   seconds, as a run's window can be under a minute."
  [direction {:keys [started-at ended-at]}]
  (let [seconds (cond-> (max 1 (js/Math.round (/ (differenceInSeconds ended-at started-at) 2)))
                  (= direction :back) -)]
    {:started-at (addSeconds started-at seconds)
     :ended-at   (addSeconds ended-at seconds)}))


(defn- update-timeline
  "Apply `f` to the stored (ISO) window. Zoom/pan live here rather than in a
   debounced view callback so repeated clicks accumulate instead of collapsing.

   Clearing the preset belongs in the branch that wrote a window: `update-in`
   creates the path it walks, so doing it unconditionally left a nil entry behind
   for a timeline that did not exist yet."
  [db master-id f]
  (if-let [{:keys [started-at ended-at]} (get-in db [:dependency-graph-timeline master-id :timeline])]
    (let [window (f {:started-at (parseISO started-at)
                     :ended-at   (parseISO ended-at)})]
      (-> db
          (assoc-in [:dependency-graph-timeline master-id :timeline]
                    {:started-at (formatISO (:started-at window))
                     :ended-at   (formatISO (:ended-at window))})
          ;; A window that has been panned or zoomed is no longer the preset that
          ;; produced it, so the period control stops claiming it is.
          (update-in [:dependency-graph-timeline master-id] dissoc :preset)))
    db))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/init
 (fn [db [_ {:keys [master-id timeline preset live?]}]]
   (if (get-in db [:dependency-graph-timeline master-id :timeline])
     db
     (update db :dependency-graph-timeline assoc master-id
             {:timeline timeline
              :preset   preset
              ;; A window that includes now opens watching.
              :live?    (boolean live?)}))))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/set-timeline
 (fn [db [_ {:keys [master-id started-at ended-at preset]}]]
   (update-in db [:dependency-graph-timeline master-id]
              (fn [entry]
                (cond-> (assoc entry
                               :timeline {:started-at started-at :ended-at ended-at}
                               ;; Remembered so the period control can name the window;
                               ;; a drag passes none, as its window is no longer the preset.
                               :preset   preset)
                  ;; choosing a period leaves a zoomed-in run behind
                  preset (dissoc :run))))))


(defn zoom-to-run
  "Zooms `entry` onto a run, dropping the range filter. `:run :previous` keeps the
   window and filter from before the first run zoomed onto, so Back skips past the
   others and restores both."
  [entry {:keys [window scheduled]}]
  (-> entry
      (assoc :timeline    window
             :subtimeline nil
             :run         {:scheduled scheduled
                           :previous  (or (get-in entry [:run :previous])
                                          (select-keys entry [:timeline :preset :subtimeline]))})
      (dissoc :preset)))

(defn back
  "Restores the window from before the run zoom."
  [entry]
  (if-let [previous (get-in entry [:run :previous])]
    (-> entry (dissoc :run) (merge previous))
    entry))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/zoom-to-run
 (fn [db [_ {:keys [master-id] :as run}]]
   (update-in db [:dependency-graph-timeline master-id] zoom-to-run run)))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/back
 (fn [db [_ {:keys [master-id]}]]
   (update-in db [:dependency-graph-timeline master-id] back)))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/set-subtimeline
 (fn [db [_ {:keys [master-id started-at ended-at]}]]
   ;; A nil window clears the sub-selection (`executions-subtimeline` treats
   ;; nil as "no filter").
   (assoc-in db [:dependency-graph-timeline master-id :subtimeline]
             (when (and started-at ended-at)
               {:started-at started-at
                :ended-at   ended-at}))))


;; Errors are keyed by request. The two fetches are dispatched together but
;; resolve independently, so a shared slot let either one's success clear the
;; other's failure — and put the log's error over the gantt, or vice versa.
(re-frame/reg-event-db
 :dependency-graph.timeline.events/set-executions
 (fn [db [_ {:keys [master-id executions]}]]
   (-> db
       (assoc-in [:dependency-graph-timeline master-id :executions] executions)
       (update-in [:dependency-graph-timeline master-id :errors] dissoc :history))))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/set-logs
 (fn [db [_ {:keys [master-id logs]}]]
   (-> db
       (assoc-in [:dependency-graph-timeline master-id :logs] logs)
       (update-in [:dependency-graph-timeline master-id :errors] dissoc :log))))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/set-error
 (fn [db [_ {:keys [master-id request error]}]]
   (assoc-in db [:dependency-graph-timeline master-id :errors request] error)))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/toggle-live
 (fn [db [_ {:keys [master-id live?]}]]
   (assoc-in db [:dependency-graph-timeline master-id :live?] live?)))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/zoom
 (fn [db [_ {:keys [master-id direction]}]]
   (update-timeline db master-id (partial zoom direction))))


(re-frame/reg-event-db
 :dependency-graph.timeline.events/pan
 (fn [db [_ {:keys [master-id direction]}]]
   (update-timeline db master-id (partial pan direction))))
